import moment from 'moment';

import { GridColumnConfig } from 'controls/grid/gridColumnConfig';
import { yesNoRenderer } from 'controls/grid/renderers/yesNoRenderer';
import { FieldType, RulesConfiguration, RulesConfigurationEntry } from 'controls/queryBuilder/ruleDefinition';
import { ApplicationState } from 'framework/applicationState';

export function mergeColumnsWithRules<DataItem>(
	rules: RulesConfiguration,
	columns: GridColumnConfig<DataItem>[],
): GridColumnConfig<DataItem>[] {
	rules ??= {};

	const configuredColumns = columns.map((column) =>
		rules[column.field] ? { ...getRuleColumn<DataItem>(column.field, rules[column.field]), ...column } : column,
	);

	const ruleOnlyColumns = Object.keys(rules)
		.filter((field) => !columns.some((column) => column.field == field))
		.map((field) => ({
			...getRuleColumn<DataItem>(field, rules[field]),
			visible: columns.length == 0,
		}));

	return [...configuredColumns, ...ruleOnlyColumns].map((column) => ({
		...column,
		title: column.title ?? column.field,
	}));
}

function getRuleColumn<DataItem>(field: string, rule: RulesConfigurationEntry): GridColumnConfig<DataItem> {
	return {
		field,
		title: rule.label,
		renderer: (item) => formatRuleValue(rule, (item as Record<string, RuleValue | RuleValue[]>)[field]),
	};
}

type RuleValue = string | number | boolean;

function formatRuleValue(rule: RulesConfigurationEntry, value: RuleValue | RuleValue[]) {
	if (value == null) return '';

	const values = Array.isArray(value) ? value : [value];

	switch (rule.type) {
		case FieldType.Boolean:
			return yesNoRenderer(!!value);
		case FieldType.Date:
			return moment(value as string | number).format(ApplicationState.momentDateTimeFormat);
		case FieldType.Select:
			return getListValueTitle(rule, value as RuleValue);
		case FieldType.MultiSelect:
			return values.map((x) => getListValueTitle(rule, x)).join(', ');
		default:
			return values.join(', ');
	}
}

function getListValueTitle(rule: RulesConfigurationEntry, value: RuleValue) {
	return rule.fieldSettings?.listValues?.find((x) => x.value == value)?.title ?? String(value);
}
